import { useState, useRef, useEffect, useId } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ArrowRight } from "lucide-react";
import { GeometricBadge } from "./GeometricBadge";

/**
 * MegaMenu — multi-column hover/focus dropdown with an optional featured card.
 * Generalizes the old single-column NavMenu. Reuses the playful tokens
 * (border-2 ink, rounded-2xl, shadow-sticker-md, pop-in). Keyboard-accessible:
 * opens on focus, closes on Esc/blur, aria-haspopup/expanded.
 *
 * Props:
 *  - label: top-level trigger text
 *  - menu:  { columns: [{ heading, items: [{icon,label,desc,to|href,badge}] }], featured? }
 *  - featured: optional override for menu.featured (used for dynamic Resources cards)
 *  - align: "left" | "right" — which edge the panel anchors to (avoid viewport overflow)
 */

function MenuItem({ item, onNavigate }) {
  const Icon = item.icon;
  const inner = (
    <>
      <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-candy/20 bg-candy/10 transition-colors group-hover:bg-candy/15">
        {Icon ? <Icon size={15} strokeWidth={2} className="text-candy" /> : null}
      </span>
      <div className="min-w-0">
        <p className="mb-0.5 flex items-center gap-1.5 font-sans text-sm font-semibold leading-none text-ink">
          {item.label}
          {item.badge ? (
            <GeometricBadge
              tone={item.badge.tone}
              size="sm"
              className="!px-1.5 !py-0.5 !tracking-[0.12em]"
            >
              {item.badge.text}
            </GeometricBadge>
          ) : null}
        </p>
        {item.desc ? (
          <p className="font-sans text-xs leading-snug text-ink/50">{item.desc}</p>
        ) : null}
      </div>
    </>
  );
  const cls =
    "group flex items-start gap-3 rounded-xl px-3 py-2.5 transition-colors hover:bg-ink/5";
  return item.href ? (
    <a href={item.href} className={cls} onClick={onNavigate}>
      {inner}
    </a>
  ) : (
    <Link to={item.to} className={cls} onClick={onNavigate}>
      {inner}
    </Link>
  );
}

function FeaturedCard({ featured, onNavigate }) {
  const body = (
    <div className="flex h-full flex-col justify-between gap-3 rounded-xl border-2 border-ink bg-candy-tint p-4 shadow-sticker-xs transition-transform duration-200 ease-bouncy hover:-translate-y-0.5">
      <div>
        <span className="t-eyebrow text-candy-dark">{featured.eyebrow}</span>
        <p className="mt-2 font-display text-lg font-extrabold leading-tight text-ink">
          {featured.title}
        </p>
        <p className="mt-1 font-sans text-xs leading-snug text-ink/70">{featured.body}</p>
      </div>
      {featured.cta ? (
        <Link
          to={featured.cta.to}
          onClick={onNavigate}
          className="inline-flex w-fit items-center gap-1.5 rounded-full border-2 border-ink bg-candy px-3 py-1.5 font-display text-xs font-bold text-white shadow-sticker-xs"
        >
          {featured.cta.label}
          <ArrowRight size={13} strokeWidth={3} />
        </Link>
      ) : (
        <span className="inline-flex items-center gap-1 font-mono text-2xs font-semibold uppercase tracking-[0.18em] text-candy-dark">
          Learn more <ArrowRight size={12} strokeWidth={3} />
        </span>
      )}
    </div>
  );
  const wrapCls = "block";
  return featured.href ? (
    <a href={featured.href} className={wrapCls} onClick={onNavigate}>
      {body}
    </a>
  ) : (
    <Link to={featured.to || "#"} className={wrapCls} onClick={onNavigate}>
      {body}
    </Link>
  );
}

export function MegaMenu({ label, menu, featured, align = "left" }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const timerRef = useRef(null);
  const id = useId();

  const show = () => {
    clearTimeout(timerRef.current);
    setOpen(true);
  };
  const hide = () => {
    timerRef.current = setTimeout(() => setOpen(false), 120);
  };
  const close = () => setOpen(false);

  useEffect(() => () => clearTimeout(timerRef.current), []);
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  const card = featured || menu.featured;
  const cols = menu.columns.length;

  return (
    <div
      className="relative"
      ref={wrapRef}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={(e) => {
        if (!wrapRef.current?.contains(e.relatedTarget)) close();
      }}
    >
      <button
        className={`inline-flex items-center gap-1 font-display text-sm font-semibold transition-colors duration-200 ${open ? "text-candy" : "text-ink hover:text-candy"}`}
        aria-haspopup="true"
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen((v) => !v)}
      >
        {label}
        <ChevronDown
          size={13}
          strokeWidth={2.5}
          className={`transition-transform duration-200 ${open ? "rotate-180 text-candy" : ""}`}
        />
      </button>

      {open && (
        <div
          id={id}
          role="menu"
          className={`absolute top-full z-50 mt-3 rounded-2xl border-2 border-ink bg-white p-3 shadow-sticker-md ${align === "right" ? "right-0" : "left-0"}`}
          style={{ animation: "pop-in 180ms cubic-bezier(0.34,1.56,0.64,1) both" }}
        >
          <div
            className="grid gap-x-6 gap-y-1"
            style={{
              gridTemplateColumns: `repeat(${cols}, minmax(190px, 1fr))${card ? " minmax(190px, 220px)" : ""}`,
            }}
          >
            {menu.columns.map((col) => (
              <div key={col.heading} role="none">
                <p className="px-3 pb-1.5 pt-1 font-mono text-2xs font-semibold uppercase tracking-[0.22em] text-ink/40">
                  {col.heading}
                </p>
                {col.items.map((item) => (
                  <MenuItem key={item.label} item={item} onNavigate={close} />
                ))}
              </div>
            ))}
            {card ? (
              <div className="pl-1">
                <FeaturedCard featured={card} onNavigate={close} />
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}

export default MegaMenu;
